import currencyUtil from '@/modules/common/utils/currencyUtil';

const DEFAULT_PAYMENT_TYPE = "monthly";

const SUBSCRIPTION_REQUIRED_PAYMENT_TYPES = ['external', 'single_charge'];

const isSubscriptionRequiredOffering = (offering) =>
  SUBSCRIPTION_REQUIRED_PAYMENT_TYPES.includes(offering?.payment_type);

/**
 * Picks the best offering for this business+SKU, or `undefined` when none is usable.
 * Priority (first match wins):
 *   1. Exact payment_type match with the active subscription — keeps `offering_uid`
 *      aligned with the subscription when an exactly-matching offering exists.
 *   2. Otherwise the first non-subscription-required offering (monthly/yearly/bundle/
 *      free) so the App Market can still let the business purchase directly.
 *   3. If none of the offerings are purchasable, return `undefined` — there is
 *      nothing the business can act on from the App Market, so callers should hide
 *      the app. (Even active external/single_charge subscriptions are managed by
 *      the partner, not from here.)
 */
const pickUsableOffering = (offeringsForSku, subscription) => {
  if (!offeringsForSku || offeringsForSku.length === 0) return undefined;

  if (subscription?.payment_type) {
    const subMatch = offeringsForSku.find(o => o.payment_type === subscription.payment_type);

    if (subMatch) return subMatch;
  }

  return offeringsForSku.find(o => !isSubscriptionRequiredOffering(o));
};

const groupOfferingsBySku = (offerings) => {
  const bySku = {};

  offerings.forEach((offering) => {
    if (!offering.SKU) return;

    (bySku[offering.SKU] = bySku[offering.SKU] || []).push(offering);
  });

  return bySku;
};

function purchasedAppData(subscription, priceInfo = null, offering = null) {
  const paymentType = subscription['payment_type'] ?? DEFAULT_PAYMENT_TYPE;

  return {
    base_price: subscription['purchase_price'],
    subscription_uid: subscription['uid'],
    price: subscription['purchase_price'],
    currency: subscription['purchase_currency'],
    payment_type: paymentType,
    purchase_state: subscription['purchase_state'],
    subscription_created_at: subscription['created_at'],
    pay_per_unit: priceInfo?.pay_per_unit ?? [],
    requires_purchase: false,
    is_free: paymentType === 'free',
    is_included: paymentType === 'bundle',
    is_external: paymentType === 'external',
    offering_uid: offering?.uid || null,
  };
}

function paidAppData(priceInfo, offering) {
  const paymentType = offering?.payment_type;
  const isFree = paymentType === 'free';
  const isBundled = paymentType === 'bundle';
  const isExternal = paymentType === 'external';

  return {
    base_price: priceInfo.price,
    price: priceInfo.price,
    currency: priceInfo.currency,
    payment_type: paymentType,
    purchase_state: 'not_purchased',
    pay_per_unit: priceInfo?.pay_per_unit ?? [],
    requires_purchase: !isFree && !isBundled && !isExternal,
    is_free: isFree,
    is_included: isBundled,
    is_external: isExternal,
    offering_uid: offering?.uid || null,
  };
}

function findOfferingPrice(geoData, offering) {
  if (!geoData) {
    return undefined;
  }

  const accountCurrency = currencyUtil.getCurrency(geoData.country, geoData.continent);
  return offering.prices?.find(item => item.currency === accountCurrency);
}

function indexSubscriptionsBySku(subscriptions) {
  const bySku = {};

  if (Array.isArray(subscriptions)) {
    subscriptions.forEach(sub => {
      if (sub.sku) {
        bySku[sub.sku] = sub;
      }
    });
  }

  return bySku;
}

/**
 * Builds a paid_data map keyed by SKU for each app that has a matching offering with a price
 * for the user's geo. Side effect: mutates `apps` by removing apps whose offering has no
 * price in the current currency (they're considered unavailable in this region).
 */
export const getPaidAppsData = (apps, offerings, subscriptions, geoData) => {
  const activeSubsBySku = indexSubscriptionsBySku(subscriptions);
  const result = {};
  const appSkus = new Set(apps.map(app => app.app_code_name));
  const offeringsBySku = groupOfferingsBySku(offerings.filter(o => o.SKU && appSkus.has(o.SKU)));

  Object.entries(offeringsBySku).forEach(([sku, offeringsForSku]) => {
    const subscription = activeSubsBySku[sku];
    const offering = pickUsableOffering(offeringsForSku, subscription);
    const priceInfo = offering ? findOfferingPrice(geoData, offering) : undefined;

    if (priceInfo) {
      if (subscription) {
        result[sku] = purchasedAppData(subscription, priceInfo, offering);
      } else {
        result[sku] = paidAppData(priceInfo, offering);
      }
    } else {
      const appIndex = apps.findIndex(app => app.app_code_name === sku);

      if (appIndex !== -1) {
        apps.splice(appIndex, 1);
      }
    }
  });

  return result;
};

export const aggregateAppDataV3 = (apps, offerings, subscriptions, geoData) => {
  const offeringsBySku = groupOfferingsBySku(offerings);
  const activeSubsBySku = indexSubscriptionsBySku(subscriptions);

  return apps.reduce((result, app) => {
    const subscription = activeSubsBySku[app.app_code_name];
    const offering = pickUsableOffering(offeringsBySku[app.app_code_name], subscription);
    const isInstalled = app.status === 'installed';

    if (!offering) {
      return result;
    }

    app.is_listed = Boolean(subscription) || offering.is_listed === true;

    const priceInfo = findOfferingPrice(geoData, offering);

    if (!priceInfo) {
      if (isInstalled) {
        app.uninstall_only = true;

        if (subscription) {
          app.paid_data = purchasedAppData(subscription);
        }
      } else {
        return result;
      }
    } else if (subscription) {
      app.paid_data = purchasedAppData(subscription, priceInfo, offering);
      app.requires_upgrade = app.requires_upgrade || app.requires_upgrade_if_paid;
    } else {
      app.paid_data = paidAppData(priceInfo, offering);
      app.requires_upgrade = app.requires_upgrade || app.requires_upgrade_if_paid;
    }

    result.push(app);
    return result;
  }, []);
};
